// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Plus, Edit, Trash2, Check, X, UserSearch } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ScoutFinanceTab from '@/components/admin-dashboard/ScoutFinanceTab';
import MultiMembershipAssignmentSelector from '@/components/admin/MultiMembershipAssignmentSelector';
import AthleteOnlyMembershipCheckboxes from '@/components/admin/AthleteOnlyMembershipCheckboxes';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Scout {
  id: string;
  name: string;
  username: string;
  email: string;
  phone?: string;
  venmo_handle?: string;
  status: 'pending' | 'active' | 'inactive';
  created_at: string;
  user_id?: string;
  revenue_percentage?: number;
}

interface Membership {
  id: string;
  name: string;
  slug: string;
}

const ScoutManagement = () => {
  const [scouts, setScouts] = useState<Scout[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedScout, setSelectedScout] = useState<Scout | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    venmo_handle: '',
    password: '',
    revenue_percentage: 90,
    status: 'active' as 'pending' | 'active' | 'inactive',
    assigned_states: [] as string[],
    assigned_sports: [] as string[],
    assigned_memberships: [] as string[]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch scouts
      const { data: scoutData, error: scoutError } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'scout')
        .order(sortField, { ascending: sortDirection === 'asc' });

      if (scoutError) throw scoutError;
      
      const scoutDataFormatted = scoutData?.map(profile => ({
        id: profile.id,
        name: profile.name,
        username: profile.username,
        email: profile.email,
        phone: profile.phone,
        venmo_handle: profile.venmo_handle,
        status: profile.status || 'active',
        created_at: profile.created_at,
        user_id: profile.user_id,
        revenue_percentage: profile.revenue_percentage || 90
      })) || [];
      
      setScouts(scoutDataFormatted);

      // Fetch athlete memberships by role instead of audience_type
      console.log('🔍 Fetching athlete memberships...');
      const { data: membershipData, error: membershipError } = await supabase
        .from('memberships')
        .select('id, name, slug, role_slug')
        .eq('role_slug', 'athlete')
        .eq('is_active', true)
        .order('name');

      if (membershipError) {
        console.error('❌ Error fetching athlete memberships:', membershipError);
        setMemberships([]);
      } else {
        console.log('🏆 Fetched athlete memberships:', membershipData);
        setMemberships(membershipData || []);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load scouts and memberships"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScoutMemberships = async (scoutId: string) => {
    console.log('🔍 Fetching memberships for scout:', scoutId);
    try {
      const { data, error } = await supabase
        .from('scout_membership_assignments')
        .select('membership_id')
        .eq('scout_id', scoutId);

      if (error) throw error;
      console.log('📋 Found existing assignments:', data);
      return data?.map(item => item.membership_id) || [];
    } catch (error) {
      console.error('❌ Error fetching scout memberships:', error);
      return [];
    }
  };

  const saveScoutMemberships = async (scoutId: string, membershipIds: string[]) => {
    console.log('🎯 saveScoutMemberships called with:', { scoutId, membershipIds });
    try {
      // Delete existing assignments
      console.log('🗑️ Deleting existing assignments for scout:', scoutId);
      const { error: deleteError } = await supabase
        .from('scout_membership_assignments')
        .delete()
        .eq('scout_id', scoutId);

      if (deleteError) {
        console.error('❌ Error deleting existing assignments:', deleteError);
        throw deleteError;
      }

      // Insert new assignments
      if (membershipIds.length > 0) {
        const assignments = membershipIds.map(membershipId => ({
          scout_id: scoutId,
          membership_id: membershipId
        }));

        console.log('💾 Inserting new assignments:', assignments);
        const { data: insertData, error: insertError } = await supabase
          .from('scout_membership_assignments')
          .insert(assignments)
          .select();

        if (insertError) {
          console.error('❌ Error inserting assignments:', insertError);
          throw insertError;
        }

        console.log('✅ Successfully inserted assignments:', insertData);
      } else {
        console.log('📝 No memberships to assign');
      }
    } catch (error) {
      console.error('Error saving scout memberships:', error);
      throw error;
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredScouts = scouts.filter(scout =>
    scout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    scout.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (scout.username && scout.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openAddDialog = () => {
    setSelectedScout(null);
    setFormData({
      name: '',
      username: '',
      email: '',
      phone: '',
      venmo_handle: '',
      password: '',
      revenue_percentage: 90,
      status: 'active',
      assigned_states: [],
      assigned_sports: [],
      assigned_memberships: []
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = async (scout: Scout) => {
    setSelectedScout(scout);
    
    // Fetch current membership assignments
    const assignedMemberships = await fetchScoutMemberships(scout.id);
    
    setFormData({
      name: scout.name,
      username: scout.username || '',
      email: scout.email,
      phone: scout.phone || '',
      venmo_handle: scout.venmo_handle || '',
      password: '', // Don't show existing password
      revenue_percentage: scout.revenue_percentage || 90,
      status: scout.status || 'active',
      assigned_states: [],
      assigned_sports: [],
      assigned_memberships: assignedMemberships
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    console.log('💾 handleSave called with formData:', formData);
    console.log('🎯 assigned_memberships:', formData.assigned_memberships);
    console.log('🔍 selectedScout:', selectedScout);
    try {
      setIsCreating(true);

      // Validation - remove the limit check
      if (formData.assigned_memberships.length === 0) {
        console.warn('⚠️ No memberships assigned to scout');
        toast({
          variant: "destructive",
          title: "Warning",
          description: "No memberships selected. Scout will not be eligible for commission calculations."
        });
      }

      if (selectedScout) {
        // Update existing scout (profile only, not auth)
        const { error } = await supabase
          .from('profiles')
          .update({
            name: formData.name,
            username: formData.username,
            email: formData.email,
            phone: formData.phone,
            venmo_handle: formData.venmo_handle,
            revenue_percentage: formData.revenue_percentage,
            status: formData.status
          })
          .eq('id', selectedScout.id);

        if (error) throw error;

        // Save membership assignments
        await saveScoutMemberships(selectedScout.id, formData.assigned_memberships);
        
        toast({
          title: "Success",
          description: "Scout updated successfully"
        });
      } else {
        // Scout account creation is not available in RecruitLook Events: the
        // inherited create-scout-account function was removed (it wrote to
        // roles/role_assignments, which do not exist in this database).
        throw new Error('Creating scout accounts is not available in this project yet.');
        const data: any = null;

        // If scout creation successful, save membership assignments
        if (data?.scout_id && formData.assigned_memberships.length > 0) {
          await saveScoutMemberships(data.scout_id, formData.assigned_memberships);
        }
        
        toast({
          title: "Success",
          description: `Scout account created successfully! They can now log in with email: ${formData.email}`
        });
      }

      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving scout:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save scout"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleStatusChange = async (scoutId: string, newStatus: Scout['status']) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', scoutId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: `Scout ${newStatus === 'active' ? 'activated' : newStatus === 'inactive' ? 'deactivated' : 'updated'}`
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating scout status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update scout status"
      });
    }
  };

  const handleDelete = async (scoutId: string) => {
    try {
      // Just update status to inactive instead of deleting
      const { error } = await supabase
        .from('profiles')
        .update({ status: 'inactive' })
        .eq('id', scoutId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Scout deactivated successfully"
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deactivating scout:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to deactivate scout"
      });
    }
  };

  const getStatusColor = (status: Scout['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'default';
      case 'inactive':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Scout Management</h1>
        <p className="text-muted-foreground">
          Manage scout accounts and their membership assignments in the platform
        </p>
      </div>

      <Tabs defaultValue="accounts" className="w-full">
        <TabsList>
          <TabsTrigger value="accounts">Scout Accounts</TabsTrigger>
          <TabsTrigger value="finance">Finance</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Scout Accounts</CardTitle>
                  <CardDescription>
                    Create and manage scout user accounts with login access and membership assignments
                  </CardDescription>
                </div>
                <Button onClick={openAddDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Scout Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search scouts by name, email, or username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-8">Loading scouts...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer"
                        onClick={() => handleSort('name')}
                      >
                        Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead 
                        className="cursor-pointer"
                        onClick={() => handleSort('email')}
                      >
                        Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Venmo</TableHead>
                      <TableHead>Revenue %</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead 
                        className="cursor-pointer"
                        onClick={() => handleSort('created_at')}
                      >
                        Created {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredScouts.map((scout) => (
                      <TableRow key={scout.id}>
                        <TableCell className="font-medium">{scout.name}</TableCell>
                        <TableCell>{scout.username || '-'}</TableCell>
                        <TableCell>{scout.email}</TableCell>
                        <TableCell>{scout.phone || '-'}</TableCell>
                        <TableCell>{scout.venmo_handle || 'Not set'}</TableCell>
                        <TableCell>{scout.revenue_percentage || 90}%</TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(scout.status)}>
                            {scout.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(scout.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {scout.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusChange(scout.id, 'active')}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusChange(scout.id, 'inactive')}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(scout)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Deactivate Scout</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to deactivate this scout? This will prevent them from logging in.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(scout.id)}>
                                    Deactivate
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {filteredScouts.length === 0 && !isLoading && (
                <div className="text-center py-8">
                  <UserSearch className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No scouts found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm ? 'Try adjusting your search terms' : 'No scout accounts have been created yet'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finance">
          <ScoutFinanceTab />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Scout Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedScout ? 'Edit Scout Account' : 'Create New Scout Account'}
            </DialogTitle>
            <DialogDescription>
              {selectedScout ? 'Update scout account information and memberships' : 'Create a new scout account with login access and membership assignments'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Scout full name"
                required
              />
            </div>

            <div>
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Unique username"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="scout@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <Label htmlFor="venmo_handle">Venmo Handle</Label>
              <Input
                id="venmo_handle"
                value={formData.venmo_handle}
                onChange={(e) => setFormData({...formData, venmo_handle: e.target.value})}
                placeholder="Enter Venmo username"
              />
            </div>

            <div>
              <Label htmlFor="revenue_percentage">Revenue Share Percentage *</Label>
              <Input
                id="revenue_percentage"
                type="number"
                min="0"
                max="100"
                value={formData.revenue_percentage}
                onChange={(e) => setFormData({...formData, revenue_percentage: parseFloat(e.target.value) || 90})}
                placeholder="90"
                required
              />
            </div>

            <div>
              <Label htmlFor="assigned_memberships">Assigned Athlete Memberships</Label>
              <AthleteOnlyMembershipCheckboxes
                memberships={memberships}
                selectedMembershipIds={formData.assigned_memberships}
                onSelectionChange={(membershipIds) => setFormData({...formData, assigned_memberships: membershipIds})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Select athlete memberships that this scout will earn commission on.
              </p>
            </div>

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: 'pending' | 'active' | 'inactive') => 
                  setFormData({...formData, status: value})
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!selectedScout && (
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter login password"
                  required
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={() => {
              console.log('🖱️ Update button clicked');
              handleSave();
            }} disabled={isCreating}>
              {isCreating ? 'Saving...' : selectedScout ? 'Update' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScoutManagement;
